import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from 'app/store';
import {
  BillingFormsInfoSliceInitialState,
  CustomerFormInfo,
  SubscriptionFormInfo
} from './billingFormsInfoTypes';
import {
  setCustomerFormInfo,
  setCustomerFormInfoAddress,
  setCustomerFormInfoAddressErrors,
  setCustomerFormInfoTaxIdErrors,
  setCustomerFormInfoTaxIds,
  setSubscriptionFormInfo,
  setSubscriptionFormInfoCouponId,
  setSubscriptionFormInfoCurrency,
  setSubscriptionFormInfoInterval,
  setSubscriptionFormInfoQuantity
} from './billingFormsInfoActions';
import { IAddress } from 'features/billing/customer/customerTypes';
import { SubscriptionPeriod } from 'features/billing/subscription/subscriptionTypes';

const initialState: BillingFormsInfoSliceInitialState = {
  subscriptionFormInfo: {
    productId: '',
    currency: '',
    interval: 'month',
    quantity: 1,
    couponId: null
  },
  customerFormInfo: {
    address: {
      country: '',
      state: '',
      line1: '',
      line2: '',
      postalCode: '',
      city: ''
    } as IAddress,
    addressErrors: {
      country: '',
      line1: '',
      postalCode: '',
      city: ''
    } as Omit<IAddress, 'line2'>,
    taxIds: {},
    taxIdErrors: {},
  }
};

export const billingFormsInfoSlice = createSlice({
  name: '@@billingFormsInfo',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(setCustomerFormInfo, (state, action: PayloadAction<CustomerFormInfo>) => {
      state.customerFormInfo = action.payload;
    });
    builder.addCase(setSubscriptionFormInfo, (state, action: PayloadAction<SubscriptionFormInfo>) => {
      state.subscriptionFormInfo = action.payload;
    });

    builder.addCase(setCustomerFormInfoAddress, (state, action: PayloadAction<IAddress>) => {
      state.customerFormInfo.address = action.payload;
    });
    builder.addCase(setCustomerFormInfoAddressErrors, (state, action: PayloadAction<Omit<IAddress, "line2">>) => {
      state.customerFormInfo.addressErrors = action.payload;
    });
    builder.addCase(setCustomerFormInfoTaxIds, (state, action: PayloadAction<Record<string, string>>) => {
      state.customerFormInfo.taxIds = action.payload;
    });
    builder.addCase(setCustomerFormInfoTaxIdErrors, (state, action: PayloadAction<Record<string, string>>) => {
      state.customerFormInfo.taxIdErrors = action.payload;
    });

    builder.addCase(setSubscriptionFormInfoCurrency, (state, action: PayloadAction<string>) => {
      state.subscriptionFormInfo.currency = action.payload;
    });
    builder.addCase(setSubscriptionFormInfoCouponId, (state, action: PayloadAction<string | null>) => {
      state.subscriptionFormInfo.couponId = action.payload;
    });
    builder.addCase(setSubscriptionFormInfoQuantity, (state, action: PayloadAction<number>) => {
      state.subscriptionFormInfo.quantity = action.payload;
    });
    builder.addCase(setSubscriptionFormInfoInterval, (state, action: PayloadAction<SubscriptionPeriod>) => {
      state.subscriptionFormInfo.interval = action.payload;
    });
  },
});

export const billingFormsInfoSelector = createSelector(
  [(state: RootState) => state.billingFormsInfo],
  (info) => info
);

export const billingFormsCustomerInfoSelector = createSelector(
  [(state: RootState) => state.billingFormsInfo.customerFormInfo],
  (address) => address
);

export const billingFormsSubscriptionInfoSelector = createSelector(
  [(state: RootState) => state.billingFormsInfo.subscriptionFormInfo],
  (addressErrors) => addressErrors
);